import { useEffect, useMemo, useState } from "react"

import { useApolloClient } from "@apollo/client"

import {
  RoomPriceDocument,
  RoomPriceQuery,
  RoomPriceQueryVariables
} from "shared/api/price/queries.generated"
import { readRoomPriceFragment } from "shared/api/price/readRoomPriceFragment"
import { useEvent } from "shared/lib/useEvent"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { RoomPrice } from "shared/types/__generated__/graphql"

import { useProjectStore } from "features/project-management/model/useProjectStore"

export function addPrices(
  ...prices: (RoomPrice | null | undefined)[]
): RoomPrice | null | undefined {
  return prices.reduce((total, price) => {
    if (total) {
      return price
        ? {
            ...total,
            priceTotal: total.priceTotal + price.priceTotal
          }
        : total
    }
    return price
  }, null)
}

export function useRoomMultiPrice({
  projectID,
  roomIDs,
  skip
}: {
  projectID: string
  roomIDs: string[]
  skip?: boolean
}) {
  const { query, cache } = useApolloClient()
  const { pendingElementIDs } = useProjectStore(projectID)

  const [roomPrices, setRoomPrices] = useState<(RoomPrice | null)[]>(
    roomIDs.map(id => readRoomPriceFragment(id, cache))
  )

  const queryMissingPrices = useEvent(() => {
    if (skip) {
      return
    }
    roomIDs.forEach(id => {
      const price = readRoomPriceFragment(id, cache)
      if (!price) {
        query<RoomPriceQuery, RoomPriceQueryVariables>({
          query: RoomPriceDocument,
          variables: { id },
          fetchPolicy: ApolloFetchPolicy.CACHE_FIRST
        })
      }
    })
  })

  const updateRoomPrices = useEvent(() => {
    setRoomPrices(roomIDs.map(id => readRoomPriceFragment(id, cache)))
  })

  useEffect(() => {
    if (pendingElementIDs.length === 0) {
      queryMissingPrices()
      updateRoomPrices()
    }
  }, [roomIDs, pendingElementIDs])

  useEffect(() => {
    if (skip) {
      return undefined
    }

    const subscriptions = roomIDs.map(id =>
      cache.watch({
        query: RoomPriceDocument,
        callback: updateRoomPrices,
        optimistic: true,
        variables: { id }
      })
    )

    return () => subscriptions.forEach(subscription => subscription())
  }, [roomIDs])

  const priceTotal = useMemo(() => {
    if (skip) {
      return undefined
    }
    const allPricesComplete =
      !roomPrices.includes(null) &&
      !roomPrices.some(roomPrice => !roomPrice?.isValid)
    if (allPricesComplete) {
      return addPrices(...roomPrices)
    }
    return undefined
  }, [roomPrices])

  return { priceTotal, isLoading: priceTotal === undefined }
}
